'use client';

import { useRef, useState } from 'react';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { FormField } from '../ui/form-field';
import MultiSelectDropdown from 'components/ui/multiselect';
import X from 'lucide-react/dist/esm/icons/x';
import DateTab from 'pages/date/Date';
import LocationDropdown from 'components/LocationDropdown/LocationDropdown';
import Upload from 'lucide-react/dist/esm/icons/upload';
import { dateFormate } from 'utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { toast, ToastContainer } from 'react-toastify';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

/**
 * CommonFormNew
 * Dynamically builds a form from a JSON config.
 * - No use-case-specific logic (no submit/state inside)
 * - Supports: input, number, select, radio, image (upload)
 * - Supports grouping/sections
 *
 * @param {Object} props
 * @param {Array} props.config - Array of field configs or group configs
 * @param {Object} props.formData - Current form data
 * @param {Function} props.onFormDataChange - Setter for form data
 */

export default function CommonFormNew({
  config = [],
  formData,
  onFormDataChange,
  onCityChange,
  cityOptions = [],
  maxTimeDuration = {},
}) {
  const fileInputRef = useRef();
  const [imageError, setImageError] = useState({});
  const [dateRange, setDateRange] = useState(
    formData.dateRange || { startDate: null, endDate: null },
  );
  const [city, setCity] = useState([]);
  const MAX_FILE_SIZE_MB = 5;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

  const updateFormData = (field, value) => {
    onFormDataChange({ ...formData, [field]: value });
    if (field === 'image') setImageError((prev) => ({ ...prev, [field]: undefined }));
  };

  let locations = useSelector((state) => state?.locationDropdown?.locations || []);

  const handleImageChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        setImageError((prev) => ({
          ...prev,
          [fieldName]: 'Only .jpg, .jpeg, .png files are allowed',
        }));
        updateFormData(fieldName, null);
        return;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        setImageError((prev) => ({
          ...prev,
          [fieldName]: 'Only image of 5MB max size is allowed',
        }));
        e.target.value = ''; // reset the input
        //toast.error('Only image of 5MB max size is allowed');
        return;
      }
      updateFormData(fieldName, {
        file,
        name: file.name,
        url: URL.createObjectURL(file),
      });
    }
  };

  const handleRemoveImage = (fieldName) => {
    updateFormData(fieldName, null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Drag & drop handlers
  const handleDrop = (e, fieldName) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        setImageError((prev) => ({
          ...prev,
          [fieldName]: 'Only .jpg, .jpeg, .png files are allowed',
        }));
        updateFormData(fieldName, null);
        return;
      }
      updateFormData(fieldName, {
        file,
        name: file.name,
        url: URL.createObjectURL(file),
      });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // DateTab handler
  const handleDateRangeChange = (value) => {
    // value is [startDate, endDate] or null
    let rangeObj = { startDate: null, endDate: null };
    if (Array.isArray(value) && value?.length === 2) {
      rangeObj = {
        startDate: value[0] || null,
        endDate: value[1] || null,
      };
    }
    setDateRange(rangeObj);
    updateFormData('dateRange', rangeObj);
  };

  const handleCityChange = (selected) => {
    let newSelected = selected;

    // Handle "All City"
    const allCity = selected.find((city) => city.id === 'all');

    if (allCity) {
      // If "All City" selected, set all cities except "All City" itself
      const fullList = (cityOptions?.length > 0 ? cityOptions : locations).filter(
        (city) => city.id !== 'all',
      );
      newSelected = fullList;
    }

    setCity(newSelected);
    updateFormData('city', newSelected);
  };

  //   // Render a single field
  const renderField = (field) => {
    if (field.type === 'input' || field.type === 'number') {
      const currentLength = (formData[field.name] || '')?.length;
      const maxLength = field.maxLength;
      return (
        <FormField
          key={field.name}
          label={field.label}
          required={field.required}
          htmlFor={field.name}
        >
          <div className="relative">
            <Input
              id={field.name}
              type={field.type === 'number' ? 'number' : 'text'}
              placeholder={field.placeholder}
              value={formData[field.name] || ''}
              onChange={(e) => {
                const value = e.target.value;
                // If maxLength is set, enforce it
                if (maxLength && value.length > maxLength) {
                  return; // Don't update if exceeds max length
                }
                updateFormData(field.name, value);
              }}
              maxLength={maxLength}
              className="h-12 text-base bg-white rounded-lg border border-gray-300 px-4 placeholder:opacity-50"
              style={{ borderRadius: '8px' }}
            />
            {maxLength && (
              <div className="text-xs text-gray-500 mt-1 text-right">
                {currentLength}/{maxLength} characters
              </div>
            )}
          </div>
        </FormField>
      );
    }

    if (field.type === 'select') {
      return (
        <FormField key={field.name} label={field.label} required={field.required}>
          <Select
            value={formData[field.name] || ''}
            onValueChange={(value) => updateFormData(field.name, value)}
          >
            <SelectTrigger className="h-12 text-base bg-white border border-gray-300">
              <SelectValue placeholder={field.placeholder} />
            </SelectTrigger>
            <SelectContent className="bg-white border border-gray-200 shadow-lg z-50">
              {(field.options || []).map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  className="text-base bg-white hover:bg-gray-50 focus:bg-gray-50 cursor-pointer"
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      );
    }

    if (field.type === 'radio') {
      return (
        <FormField key={field.name} label={field.label} required={field.required}>
          <div className="flex gap-4">
            {(field.options || []).map((option) => (
              <label key={option.value} className="flex items-center gap-2">
                <input
                  type="radio"
                  name={field.name}
                  value={option.value}
                  checked={formData[field.name] === option.value}
                  onChange={() => updateFormData(field.name, option.value)}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        </FormField>
      );
    }

    if (field.type === 'image') {
      const hasError = imageError[field.name];

      return (
        <FormField key={field.name} label={field.label} htmlFor={field.name}>
          <div
            className={`flex flex-col items-center justify-center bg-[#D1E7FA] rounded-lg border border-blue-200 py-8 px-4 relative transition-all w-full`}
            style={{ minHeight: 180, cursor: 'pointer', borderRadius: '8px' }}
            onDrop={(e) => handleDrop(e, field.name)}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            {!formData[field.name] && (
              <>
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-black mb-6">
                    <Upload size={20} />
                  </div>
                  <span className="text-gray-600 mb-2 text-center text-md">
                    Use an image in JPG, PNG.
                    <br /> 5 MB max
                  </span>
                  <button
                    type="button"
                    className="mt-2 px-4 py-2 border border-[color:var(--brand-primary)] rounded text-blue-700 font-medium hover:bg-blue-200"
                  >
                    Choose File
                  </button>
                </div>
                <Input
                  id={field.name}
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={(e) => handleImageChange(e, field.name)}
                  className="hidden"
                />
              </>
            )}
            {formData[field.name] && formData[field.name].url && (
              <>
                <div className="flex flex-col items-center gap-2 relative">
                  <img
                    src={formData[field.name].url}
                    alt={formData[field.name].name}
                    className="w-32 h-20 object-cover rounded border"
                  />
                  <span className="text-sm text-gray-600 flex-1 truncate">
                    {formData[field.name].name}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveImage(field.name);
                  }}
                  className="absolute top-[1rem] right-[11rem] bg-white rounded-full p-1 shadow hover:bg-gray-100"
                  aria-label="Remove"
                >
                  <X size={18} className="text-red-500" />
                </button>
              </>
            )}
            {hasError && (
              <div className="text-xs text-red-500 mt-2 absolute bottom-2 left-1/2 -translate-x-1/2">
                {hasError}
              </div>
            )}
          </div>
        </FormField>
      );
    }

    // Date range (use DateTab) - Fixed with FormField wrapper
    if (field.type === 'dateRange') {
      return (
        <FormField key={field.name} label={field.label} required={field.required}>
          <Box className="customDateRangePicker">
            <DateTab
              className="w-full bg-white"
              onSelectDateRange={handleDateRangeChange}
              dateFrom={dateRange.startDate}
              dateTo={dateRange.endDate}
              minDate={
                field.minDate
                  ? field.minDate.toDate()
                  : maxTimeDuration?.startDate
                    ? new Date(maxTimeDuration?.startDate)
                    : new Date()
              }
              maxDate={maxTimeDuration?.endDate ? new Date(maxTimeDuration?.endDate) : null}
              allowFutureDates
              fullWidth
              style={{ borderRadius: '8px', width: '100%' }}
            />
          </Box>
        </FormField>
      );
    }

    // City dropdown - Fixed with FormField wrapper
    if (field.type === 'city') {
      return (
        <FormField key={field.name} label={field.label || 'City'} required={field.required}>
          <Box className="customDateRangePicker">
            <div className="relative z-0">
              <MultiSelectDropdown
                options={cityOptions?.length > 0 ? cityOptions : locations}
                value={city}
                onChange={handleCityChange}
                label="City"
                placeholder="City"
                className="w-full"
                fullWidth
              />
            </div>
          </Box>
        </FormField>
      );
    }

    if (field.type === 'date') {
      return (
        <FormField key={field.name} label={field.label} required={field.required}>
          <DatePicker
            value={formData[field.name] ? dayjs(formData[field.name], 'YYYY-MM-DD') : null}
            onChange={(newValue) => {
              // Store as YYYY-MM-DD string
              updateFormData(field.name, newValue ? newValue.format('YYYY-MM-DD') : null);
            }}
            minDate={dayjs()}
            shouldDisableDate={(date) => date.isSame(dayjs(), 'day')}
            slotProps={{
              textField: {
                fullWidth: true,
                placeholder: field.placeholder || 'Select Date',
                sx: { width: '100%' },
              },
            }}
          />
        </FormField>
      );
    }
    // fallback for unknown field types
    return null;
  };

  // Main render (flat, not grouped, for this use-case)
  return <div className="space-y-6 min-h-[380px]">{config.map(renderField)}</div>;
}
